import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useEasterEggStore } from './useEasterEggStore';
import { useEasterEggSync } from './useEasterEggSync';

const EGG_COLORS = [
  { base: '#FFB7C5', stripe1: '#FF8FA3', stripe2: '#FFEAA7', dots: '#C3AED6' },
  { base: '#A8E6CF', stripe1: '#7BC8A4', stripe2: '#FFD3B6', dots: '#FFB7C5' },
  { base: '#C3AED6', stripe1: '#9B8EC0', stripe2: '#FFB7C5', dots: '#FFEAA7' },
  { base: '#FFEAA7', stripe1: '#FFD166', stripe2: '#FFB7C5', dots: '#A8E6CF' },
  { base: '#B5EAD7', stripe1: '#8DD5B4', stripe2: '#FFDAC1', dots: '#C7CEEA' },
  { base: '#FF9AA2', stripe1: '#FF6F7A', stripe2: '#FFDAC1', dots: '#B5EAD7' },
  { base: '#C7CEEA', stripe1: '#A0AAD6', stripe2: '#FFB7C5', dots: '#FFEAA7' },
  { base: '#FFDAC1', stripe1: '#FFB89A', stripe2: '#C7CEEA', dots: '#B5EAD7' },
];

const EGG_SIZES = [12, 13, 14, 16, 19, 22, 26, 29];

type EggData = {
  id: string;
  x: number;
  y: number;
  size: number;
  color: (typeof EGG_COLORS)[number];
  rotation: number;
  pattern: 'stripes' | 'dots' | 'zigzag';
  fadeDelay: number;
};

const KEYFRAMES_CSS = `
@keyframes easterEggPop {
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.5); opacity: 0.6; }
  100% { transform: scale(0); opacity: 0; }
}
@keyframes easterEggPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}
`;

const generateEggs = (count: number, foundEggIds: Set<string>, seed: string): EggData[] => {
  // 40% chance of no eggs on this page
  if (Math.random() < 0.4) return [];

  const eggCount = 1 + Math.floor(Math.random() * count);

  // Divide the screen into a grid to spread eggs out
  const cols = 3;
  const rows = 3;
  const cells: { x: number; y: number }[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      cells.push({
        x: (c / cols) * 80 + 5 + Math.random() * (80 / cols),
        y: (r / rows) * 70 + 8 + Math.random() * (70 / rows),
      });
    }
  }
  // Shuffle cells and pick positions
  for (let i = cells.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cells[i], cells[j]] = [cells[j], cells[i]];
  }

  const rotations = [0, -25, 15, 0, 30, -15, 0, -10, 20, 0];
  const eggs: EggData[] = [];
  let lastDelay = -5000;
  for (let i = 0; i < eggCount; i++) {
    const id = `egg-${seed}-${Date.now()}-${i}`;
    if (foundEggIds.has(id)) continue;

    const minDelay = Math.max(2000, lastDelay + 5000);
    const fadeDelay = minDelay + Math.random() * Math.max(0, 10_000 - minDelay);
    lastDelay = fadeDelay;

    const pos = cells[i % cells.length];
    eggs.push({
      id,
      x: pos.x,
      y: pos.y,
      size: EGG_SIZES[Math.floor(Math.random() * EGG_SIZES.length)],
      color: EGG_COLORS[Math.floor(Math.random() * EGG_COLORS.length)],
      rotation: rotations[i % rotations.length],
      pattern: (['stripes', 'dots', 'zigzag'] as const)[Math.floor(Math.random() * 3)],
      fadeDelay,
    });
  }
  return eggs;
};

const EasterEggSvg = ({
  egg,
  onClick,
  onExpire,
}: {
  egg: EggData;
  onClick: (id: string) => void;
  onExpire: (id: string) => void;
}) => {
  const [popping, setPopping] = useState(false);
  const [visible, setVisible] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const { size, color, pattern } = egg;
  const w = size;
  const h = size * 1.3;

  useEffect(() => {
    const fadeInTimer = setTimeout(() => setVisible(true), egg.fadeDelay);
    return () => clearTimeout(fadeInTimer);
  }, [egg.fadeDelay]);

  useEffect(() => {
    if (!visible) return;
    const expireTimer = setTimeout(() => {
      setFadingOut(true);
      setTimeout(() => onExpire(egg.id), 1000);
    }, 30_000);
    return () => clearTimeout(expireTimer);
  }, [visible, egg.id, onExpire]);

  const handleClick = () => {
    setPopping(true);
    setTimeout(() => onClick(egg.id), 350);
  };

  return (
    <div
      style={{
        position: 'fixed',
        left: `${egg.x}%`,
        top: `${egg.y}%`,
        zIndex: 9998,
        transform: `rotate(${egg.rotation}deg)`,
        filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15))',
        opacity: fadingOut ? 0 : visible ? 1 : 0,
        transition: fadingOut ? 'opacity 1s ease-in-out' : 'opacity 0.5s ease-in-out',
        pointerEvents: visible && !fadingOut ? 'auto' : 'none',
      }}
    >
      <div
        style={{
          cursor: 'pointer',
          transition: 'filter 0.2s, transform 0.2s',
          animation: popping ? 'easterEggPop 0.35s forwards' : 'easterEggPulse 4s ease-in-out infinite',
        }}
        onClick={handleClick}
        onMouseEnter={(e) => {
          e.currentTarget.style.filter = `brightness(1.1)`;
          e.currentTarget.style.transform = 'scale(1.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.filter = '';
          e.currentTarget.style.transform = '';
        }}
        role='button'
        aria-label='Easter egg - click to collect!'
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleClick();
        }}
      >
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} xmlns='http://www.w3.org/2000/svg'>
          <ellipse cx={w / 2} cy={h * 0.55} rx={w / 2} ry={h * 0.45} fill={color.base} />
          <ellipse cx={w / 2} cy={h * 0.42} rx={w * 0.44} ry={h * 0.38} fill={color.base} />

          {pattern === 'stripes' && (
            <>
              <ellipse cx={w / 2} cy={h * 0.38} rx={w * 0.38} ry={h * 0.04} fill={color.stripe1} />
              <ellipse cx={w / 2} cy={h * 0.52} rx={w * 0.44} ry={h * 0.04} fill={color.stripe2} />
              <ellipse cx={w / 2} cy={h * 0.66} rx={w * 0.38} ry={h * 0.04} fill={color.stripe1} />
            </>
          )}
          {pattern === 'dots' && (
            <>
              <circle cx={w * 0.3} cy={h * 0.4} r={w * 0.06} fill={color.dots} />
              <circle cx={w * 0.7} cy={h * 0.4} r={w * 0.06} fill={color.dots} />
              <circle cx={w * 0.5} cy={h * 0.55} r={w * 0.06} fill={color.stripe1} />
              <circle cx={w * 0.3} cy={h * 0.68} r={w * 0.06} fill={color.dots} />
              <circle cx={w * 0.7} cy={h * 0.68} r={w * 0.06} fill={color.dots} />
            </>
          )}
          {pattern === 'zigzag' && (
            <path
              d={`M${w * 0.15} ${h * 0.45} L${w * 0.3} ${h * 0.38} L${w * 0.45} ${h * 0.45} L${w * 0.6} ${h * 0.38} L${w * 0.75} ${h * 0.45} L${w * 0.85} ${h * 0.4}`}
              stroke={color.stripe1}
              strokeWidth={2}
              fill='none'
            />
          )}

          <ellipse cx={w * 0.35} cy={h * 0.3} rx={w * 0.1} ry={h * 0.08} fill='white' opacity='0.4' />
        </svg>
      </div>
    </div>
  );
};

const EGGS_PER_PAGE = 2;

export const EasterEggHunt = () => {
  const location = useLocation();
  const { collectEgg, foundEggIds } = useEasterEggStore();
  useEasterEggSync();
  const [eggs, setEggs] = useState<EggData[]>([]);
  const foundIdsRef = useRef(foundEggIds);
  foundIdsRef.current = foundEggIds;

  const rootSegment = location.pathname.split('/')[1] ?? '';

  useEffect(() => {
    setEggs(generateEggs(EGGS_PER_PAGE, foundIdsRef.current, rootSegment));
  }, [rootSegment]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (Math.random() >= 0.4) return;

      const id = `egg-bonus-${rootSegment}-${Date.now()}`;
      if (foundIdsRef.current.has(id)) return;

      const rotations = [0, -25, 15, 30, -15, -10, 20];
      const egg: EggData = {
        id,
        x: 5 + Math.random() * 80,
        y: 8 + Math.random() * 70,
        size: EGG_SIZES[Math.floor(Math.random() * EGG_SIZES.length)],
        color: EGG_COLORS[Math.floor(Math.random() * EGG_COLORS.length)],
        rotation: rotations[Math.floor(Math.random() * rotations.length)],
        pattern: (['stripes', 'dots', 'zigzag'] as const)[Math.floor(Math.random() * 3)],
        fadeDelay: 0,
      };
      setEggs((prev) => [...prev, egg]);
    }, 240_000);
    return () => clearTimeout(timer);
  }, [rootSegment]);

  const handleCollect = useCallback(
    (id: string) => {
      collectEgg(id);
      setEggs((prev) => prev.filter((e) => e.id !== id));
    },
    [collectEgg],
  );

  const handleExpire = useCallback((id: string) => {
    setEggs((prev) => prev.filter((e) => e.id !== id));
  }, []);

  return (
    <>
      <style>{KEYFRAMES_CSS}</style>
      {eggs.map((egg) => (
        <EasterEggSvg key={egg.id} egg={egg} onClick={handleCollect} onExpire={handleExpire} />
      ))}
    </>
  );
};
